import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';
import { getData } from 'src/services/RestApi';
import SearchSelectField from 'src/components/widgets/search-select-field';
import {
  setCacheTrackings,
  getCacheTrackings,
} from 'src/utils/cache-data';
import { useOptions } from 'src/hooks/use-privileges';

export const DropdownTracking = forwardRef(({
  disabled = false,
  label = 'tracking',
  name,
  hintValue,
  onSelected,
  triggerReset,
  filterValues = [],
  showClearIcon = false,
}, ref) => {
  const [data, setData] = useState([]);
  const [defaultVal, setDefaultVal] = useState(null);
  const isFetchingData = useRef(false);
  const { multi } = useOptions('td'); 

  const bindToView = (items) => {
    let filtered = items;
    if (Array.isArray(filterValues) && filterValues.length > 0) {
      const filter = filterValues.map(String);
      // filtered = items.filter(item => filter.includes(item.value));
      filtered = filtered.filter(item =>
        item?.value !== undefined && filter.includes(String(item.value))
      );
    }

    if (Array.isArray(multi) && multi.length > 0) {
        filtered = filtered.filter(item =>
          item?.value !== undefined && multi.includes(item.value)
      );
    }

    setData(filtered);
  };
  
  const fetchData = useCallback(async () => {
    if (!isFetchingData.current) {
      isFetchingData.current = true;
      try {
        const { result } = await getData('fixed/tracking/cache');
        if (result) {
          bindToView(result);
          setCacheTrackings(result);
        }
      } catch (error) {
        // console.error('Failed to fetch data:', error);
      } finally {
        isFetchingData.current = false;
      }
    }
  }, []);

  useEffect(() => {
    const savedData = getCacheTrackings();
    if (savedData) {
      bindToView(savedData);
    } else {
      fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    if (triggerReset) {
      const reloadValue = () => {
        setDefaultVal(null);
      };
      triggerReset(reloadValue);
    }
  }, [triggerReset]);

  const handleChanged = (selectedOption) => {
    if (onSelected) {
      onSelected(selectedOption);
    }
  };

  const selectRef = useRef(null);
  useImperativeHandle(ref, () => ({
    handleSelect: (key) => {
      const match = data.find(item =>
        item.value === key
      );
      if (match) {
        setDefaultVal(match);
        onSelected?.(match);
      }
    },
    reset: () => {
      // console.log('SearchSelectField: reset called');
      selectRef.current?.reset?.();
      setDefaultVal(null);
    } 
  }));

  return (
    <SearchSelectField
      ref={selectRef}
      label={label}
      name={name}
      disabled={disabled}
      options={data}
      onSelected={onSelected}
      defaultValue={defaultVal}
      hintValue={hintValue || ''}
      showClearIcon={showClearIcon}
    />
  );
});

DropdownTracking.propTypes = {
  name: PropTypes.string.isRequired,
  onSelected: PropTypes.func,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  hintValue: PropTypes.string,
  triggerReset: PropTypes.func,
};

export default DropdownTracking;
